<script>
    import { onMount } from 'svelte';
    import pb from '../lib/pocketbase';

    export let newEntity = { atributos: [] };
    export let entityName;
    export let canModify = false;
    export let canCreate = false; // Nueva prop para crear
    export let currentEntityId = null;

    let editMode = {};
    let updatedValues = {};
    let availableAtributos = [];
    let selectedAtributoId = null;
    let validationErrors = {}; // To track validation errors for each attribute

    const fetchAtributos = async () => {
        try {
            const res = await pb.collection('atributo').getFullList({
                filter: `tipo="${entityName}"`,
            });
            availableAtributos = res.map(attr => ({
                id: attr.id,
                descrip: attr.descrip,
                tipo_dato: attr.tipo_dato,
                requerido: attr.requerido,
            }));
        } catch (error) {
            console.error('Error al cargar atributos:', error);
        }
    };

    const enableEdit = (id, valor) => {
        editMode = { ...editMode, [id]: true };
        updatedValues = { ...updatedValues, [id]: valor };
        // Clear previous error message
        validationErrors[id] = '';
    };

    const saveEdit = (id) => {
        const index = newEntity.atributos.findIndex(attr => attr.atributo.id === id);
        if (index !== -1) {
            const atributo = newEntity.atributos[index].atributo;

            // Validation: Check if the attribute is required and if the value is empty
            if (atributo.requerido && !updatedValues[id]) {
                validationErrors[id] = `El atributo "${atributo.descrip}" es requerido.`;
                return; // Exit the function if validation fails
            } else {
                validationErrors[id] = ''; // Clear error if validation passes
            }

            newEntity.atributos[index].valor = updatedValues[id];
            newEntity = { ...newEntity, atributos: [...newEntity.atributos] };
        }
        editMode = { ...editMode, [id]: false };
    };

    const discardEdit = (id) => {
        editMode = { ...editMode, [id]: false };
        updatedValues[id] = ''; // Clear updated values if necessary
    };

    const handleInputChange = (id, value) => {
        updatedValues = { ...updatedValues, [id]: value };
        // Clear error message when user starts typing
        if (validationErrors[id]) {
            validationErrors[id] = '';
        }
    };

    onMount(() => {
        fetchAtributos();
    });

    const crearAtributo = () => {
        if (selectedAtributoId) {
            const atributoSeleccionado = availableAtributos.find(attr => attr.id === selectedAtributoId);
            const nuevoAtributo = {
                atributo: { 
                    id: atributoSeleccionado.id, 
                    descrip: atributoSeleccionado.descrip, 
                    tipo_dato: atributoSeleccionado.tipo_dato,
                    requerido: atributoSeleccionado.requerido,
                },
                valor: ''
            };
            if (!newEntity.atributos.some(attr => attr.atributo.id === nuevoAtributo.atributo.id)) {
                newEntity.atributos = [...newEntity.atributos, nuevoAtributo];
                selectedAtributoId = null;
            }
        }
    };

    const eliminarAtributo = (id) => {
        newEntity.atributos = newEntity.atributos.filter(attr => attr.atributo.id !== id);
        delete validationErrors[id]; // Remove any validation error for the removed attribute
    };

    // Convert date to Guatemala timezone
    function parseAndAdjustDate(dateString) {
        const date = new Date(dateString);
        if (isNaN(date)) return new Date();
        return date; // Return the original date, formatting will handle timezone
    }

    function formatDate(date) {
        if (!(date instanceof Date) || isNaN(date)) return '';
        
        // Convert from UTC to Guatemala timezone (UTC-6)
        const guatemalaDate = new Date(date.getTime() - (6 * 60 * 60 * 1000));
        
        const day = String(guatemalaDate.getUTCDate()).padStart(2, '0');
        const month = String(guatemalaDate.getUTCMonth() + 1).padStart(2, '0');
        const year = guatemalaDate.getUTCFullYear();
        
        return `${day}/${month}/${year}`;
    }
</script>

<div class="text-gray-600">
    <h3 class="text-lg font-bold mb-4">Atributos</h3>

    <div class="mb-4">
        <select bind:value={selectedAtributoId} class="border p-1 mr-2" disabled={!canModify && (!canCreate || currentEntityId) }>
            <option value="" selected>Seleccionar atributo</option>
            {#each availableAtributos as { id, descrip }}
                {#if !newEntity.atributos.some(attr => attr.atributo.id === id)}
                    <option value={id}>{descrip}</option>
                {/if}
            {/each}
        </select>

        {#if canModify || canCreate}
            <button class="bg-lightBlue-500 text-white px-4 py-2 rounded" on:click={crearAtributo}>
                Crear
            </button>
        {/if}
    </div>

    {#if newEntity.atributos.length > 0}
        <table class="items-center w-full bg-transparent border-collapse">
            <thead>
                <tr class="bg-gray-100">
                    <th class="px-6 py-3 text-xs uppercase border border-l-0 border-r-0 font-semibold">Descripción</th>
                    <th class="px-6 py-3 text-xs uppercase border border-l-0 border-r-0 font-semibold">Tipo de dato</th>
                    <th class="px-6 py-3 text-xs uppercase border border-l-0 border-r-0 font-semibold">Valor</th>
                    <th class="px-6 py-3 text-xs uppercase border border-l-0 border-r-0 font-semibold">Acciones</th>
                </tr>
            </thead>
            <tbody>
                {#each newEntity.atributos as { atributo, valor }}
                    <tr>
                        <td class="px-6 py-3 text-xs">{atributo.descrip}</td>
                        <td class="px-6 py-3 text-xs">{atributo.tipo_dato}</td>
                        <td class="px-6 py-3 text-xs">
                            {#if editMode[atributo.id]}
                                {#if atributo.tipo_dato === 'fecha'}
                                    <input
                                        type="date"
                                        class="border border-gray-300 rounded p-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        bind:value={updatedValues[atributo.id]}
                                        on:input={(e) => handleInputChange(atributo.id, e.target.value)}
                                        placeholder="dd-mm-yyyy"
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                    />
                                {:else if atributo.tipo_dato === 'booleano'}
                                    <input
                                        type="checkbox"
                                        class="border p-1"
                                        checked={updatedValues[atributo.id] === 'true'}
                                        id={atributo.id}
                                        on:change={(e) => handleInputChange(atributo.id, e.target.checked ? 'true' : 'false')}
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                    />
                                {:else if atributo.tipo_dato === 'lista'}
                                    <select
                                        id={atributo.id} 
                                        class="border p-1 w-full"
                                        bind:value={updatedValues[atributo.id]}
                                        on:blur={(e) => handleInputChange(atributo.id, e.target.value)} 
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                    >
                                        {#each atributo.lista as item}
                                            <option value={item}>{item}</option>
                                        {/each}
                                    </select>
                                {:else if atributo.tipo_dato === 'numerico'}
                                    <input
                                        type="number"
                                        class="border border-gray-300 rounded p-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        bind:value={updatedValues[atributo.id]}
                                        on:input={(e) => handleInputChange(atributo.id, e.target.value)}
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                    />
                                {:else}
                                    <input
                                        type="text"
                                        class="border border-gray-300 rounded p-0.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        bind:value={updatedValues[atributo.id]}
                                        on:input={(e) => handleInputChange(atributo.id, e.target.value)}
                                        disabled={!canModify && (!canCreate || currentEntityId) }
                                    />
                                {/if}
                                {#if validationErrors[atributo.id]}
                                    <p class="text-red-500 text-sm mt-1">{validationErrors[atributo.id]}</p>
                                {/if}
                            {:else if atributo.tipo_dato === 'fecha'}
                                {formatDate(parseAndAdjustDate(valor))}
                            {:else if valor === "true"}
                                <p>Sí</p>
                            {:else if valor === "false"}
                                <p>No</p>
                            {:else}
                                {valor}
                            {/if}
                        </td>

                        {#if (canModify && currentEntityId) || canCreate}
                            <td class="px-6 py-3 text-xs">
                                {#if editMode[atributo.id]}
                                    <div class="inline-flex space-x-3">
                                        <button class="bg-blueGray-600 text-white px-2 py-1 rounded" on:click={() => saveEdit(atributo.id)}>
                                            Guardar
                                        </button>
                                        &nbsp;
                                        <button class="bg-red-400 text-white px-2 py-1 rounded" on:click={() => discardEdit(atributo.id)}>
                                            Descartar
                                        </button>
                                    </div>
                                {:else}
                                    <div class="inline-flex space-x-3">
                                        <button class="bg-yellow-500 text-white px-2 py-1 rounded" on:click={() => enableEdit(atributo.id, valor)}>
                                            Editar
                                        </button>
                                        &nbsp;
                                        <button class="bg-red-500 text-white px-2 py-1 rounded" on:click={() => eliminarAtributo(atributo.id)}>
                                            Eliminar
                                        </button>
                                    </div>
                                {/if}
                            </td>
                        {/if}
                    </tr>
                {/each}
            </tbody>
        </table>
    {:else}
        <p>No hay atributos disponibles.</p>
    {/if}
</div>